import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Play, Loader2, RefreshCw, AlertCircle, ShieldCheck } from 'lucide-react';
import { pipelinesApi } from '../api/client';
import EnvCheckModal from '../components/Pipeline/EnvCheckModal';

const REPOS = ['cloud-server', 'cloud-admin', 'cloud-frontend', 'cloud-api', 'cloud-worker'];

const DEFAULT_PIPELINE = {
  name: '',
  repo: '',
  branch: 'main',
  environments: ['dev', 'staging', 'production'],
  triggerBranches: '',
  requireApproval: false,
};

// ── Cloud Build trigger panel ────────────────────────────────────────────────
function CloudBuildPanel({ repo, branch }) {
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [subsValues, setSubsValues] = useState({});
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);
  const [triggerError, setTriggerError] = useState(null);
  const [envModal, setEnvModal] = useState({ open: false, missingKeys: [] });
  const lastRepo = useRef(null);

  function fetchConfig(force = false) {
    if (!repo) return;
    if (!force && lastRepo.current === repo && Object.keys(subsValues).length) return;
    lastRepo.current = repo;
    setLoading(true);
    setFetchError(null);
    pipelinesApi.getCloudBuildConfig(repo)
      .then(cfg => {
        setSubsValues(prev =>
          force
            ? Object.fromEntries(Object.entries(cfg.substitutions).map(([k, v]) => [k, prev[k] ?? v]))
            : { ...cfg.substitutions }
        );
      })
      .catch(e => setFetchError(e.message || 'Failed to load trigger config'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { if (repo) fetchConfig(); }, [repo]); // eslint-disable-line

  function doTrigger() {
    setTriggering(true);
    setTriggerError(null);
    setTriggerResult(null);
    pipelinesApi.triggerDirect({ repo, branch, substitutions: subsValues })
      .then(r => setTriggerResult(r))
      .catch(e => setTriggerError(e.error || e.message || 'Trigger failed'))
      .finally(() => setTriggering(false));
  }

  async function handleTrigger() {
    setTriggering(true);
    setTriggerError(null);
    setTriggerResult(null);
    try {
      const { missingKeys } = await pipelinesApi.envCheck({ repo, branch });
      if (missingKeys.length > 0) {
        setEnvModal({ open: true, missingKeys });
        setTriggering(false);
        return;
      }
    } catch {
      // env-check failure is non-blocking
    }
    doTrigger();
  }

  if (!repo) {
    return (
      <p className="text-xs text-slate-500 text-center py-6">Select a repository to see Cloud Build config.</p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Substitutions */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-700">
          Cloud Build Substitutions
          {loading && <span className="ml-2 text-slate-500 font-normal">Loading…</span>}
        </span>
        <button
          onClick={() => fetchConfig(true)}
          disabled={loading}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-40"
        >
          <RefreshCw size={10} className={loading ? 'animate-spin' : ''} />
          Reload
        </button>
      </div>

      {fetchError && (
        <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          <AlertCircle size={12} className="text-red-400 shrink-0 mt-0.5" />
          <span className="text-xs text-red-400">{fetchError}</span>
        </div>
      )}

      {Object.keys(subsValues).length > 0 && (
        <div className="space-y-2.5">
          {Object.entries(subsValues).map(([key, val]) => (
            <div key={key}>
              <label className="block text-[10px] font-mono text-blue-700 mb-0.5">{key}</label>
              <input
                className="input text-xs font-mono"
                value={val}
                placeholder="(empty)"
                onChange={e => setSubsValues(s => ({ ...s, [key]: e.target.value }))}
              />
            </div>
          ))}
        </div>
      )}

      {!loading && !fetchError && Object.keys(subsValues).length === 0 && (
        <p className="text-xs text-slate-500">No substitutions found in trigger config.</p>
      )}

      {/* Trigger */}
      <div className="border-t border-slate-200 pt-4 space-y-3">
        {triggerError && (
          <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            <AlertCircle size={12} className="text-red-400 shrink-0 mt-0.5" />
            <span className="text-xs text-red-400">{triggerError}</span>
          </div>
        )}

        {triggerResult && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2 space-y-1">
            <p className="text-xs text-green-400 font-medium">Build triggered!</p>
            <p className="text-[10px] text-green-600">Status: {triggerResult.buildStatus}</p>
            <Link
              to={`/builds/${triggerResult.buildId}`}
              className="block text-[10px] font-mono text-purple-400 hover:text-purple-300 underline underline-offset-2 transition-colors"
            >
              View logs → {triggerResult.buildId}
            </Link>
          </div>
        )}

        <button
          onClick={handleTrigger}
          disabled={!branch || triggering || loading}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium bg-accent-purple hover:bg-accent-purple-light disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
        >
          {triggering
            ? <><Loader2 size={13} className="animate-spin" /> Checking…</>
            : <><Play size={13} /> Trigger Build</>
          }
        </button>
        {!branch && <p className="text-[10px] text-slate-500 text-center">Enter a branch to enable trigger</p>}
      </div>

      {envModal.open && (
        <EnvCheckModal
          missingKeys={envModal.missingKeys}
          repo={repo}
          onConfirm={() => { setEnvModal({ open: false, missingKeys: [] }); doTrigger(); }}
          onCancel={() => setEnvModal({ open: false, missingKeys: [] })}
        />
      )}
    </div>
  );
}

// ── Main editor ──────────────────────────────────────────────────────────────
export default function PipelineEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [pipeline, setPipeline] = useState(DEFAULT_PIPELINE);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  // dependency state
  const [allPipelines, setAllPipelines] = useState([]);
  const [selectedDeps, setSelectedDeps] = useState([]); // array of pipeline IDs

  useEffect(() => {
    pipelinesApi.list()
      .then(ps => setAllPipelines(ps.filter(p => p.id !== id)))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!isNew) {
      Promise.all([
        pipelinesApi.get(id),
        pipelinesApi.getDependencies(id),
      ])
        .then(([pl, deps]) => {
          setPipeline(pl);
          setSelectedDeps(deps.map(d => d.id));
        })
        .catch(() => navigate('/triggers'))
        .finally(() => setLoading(false));
    }
  }, [id, isNew, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      let savedId = id;
      if (isNew) {
        const created = await pipelinesApi.create(pipeline);
        savedId = created.id;
        navigate(`/triggers/${savedId}/edit`, { replace: true });
      } else {
        await pipelinesApi.update(id, pipeline);
      }
      await pipelinesApi.setDependencies(savedId, selectedDeps);
    } catch (err) {
      alert(`Save failed: ${err.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleDep = (depId) => {
    setSelectedDeps(prev =>
      prev.includes(depId) ? prev.filter(x => x !== depId) : [...prev, depId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="text-accent-purple animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] animate-fade-in">
      {/* Top bar */}
      <div className="shrink-0 border-b border-slate-200 px-6 py-3 flex items-center gap-4 bg-surface-1">
        <Link to="/triggers" className="btn-ghost py-1.5 px-2.5 text-xs">
          <ArrowLeft size={14} /> Back
        </Link>
        <h1 className="text-sm font-semibold text-slate-900">
          {isNew ? 'New Pipeline' : (pipeline.name || 'Edit Pipeline')}
        </h1>
        <button onClick={handleSave} disabled={saving} className="btn-primary py-1.5 px-3 text-xs ml-auto">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          Save
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Left column: persistent settings ── */}
          <div className="space-y-5">

            {/* Basic info */}
            <div className="card p-5 space-y-4">
              <h3 className="text-sm font-semibold text-slate-900">Pipeline Info</h3>

              <div>
                <label className="text-xs text-slate-400 mb-1 block">Name</label>
                <input
                  className="input"
                  placeholder="e.g. cloud-server deploy"
                  value={pipeline.name}
                  onChange={e => setPipeline(p => ({ ...p, name: e.target.value }))}
                />
              </div>

              <div>
                <label className="text-xs text-slate-400 mb-1 block">Repository</label>
                <select
                  className="select"
                  value={pipeline.repo}
                  onChange={e => setPipeline(p => ({ ...p, repo: e.target.value }))}
                >
                  <option value="">Select repo…</option>
                  {REPOS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs text-slate-400 mb-1 block">Default Branch</label>
                <input
                  className="input"
                  placeholder="main"
                  value={pipeline.branch}
                  onChange={e => setPipeline(p => ({ ...p, branch: e.target.value }))}
                />
              </div>
            </div>

            {/* Dependencies */}
            <div className="card p-5 space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Depends On</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  These services must finish successfully before this one starts during a branch deploy.
                </p>
              </div>

              {allPipelines.length === 0 ? (
                <p className="text-xs text-slate-500">No other pipelines available.</p>
              ) : (
                <div className="space-y-2">
                  {allPipelines.map(p => (
                    <label key={p.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        className="accent-accent-purple"
                        checked={selectedDeps.includes(p.id)}
                        onChange={() => toggleDep(p.id)}
                      />
                      <div>
                        <span className="text-sm text-slate-900">{p.name}</span>
                        <span className="ml-2 text-xs font-mono text-slate-500">{p.repo}</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Trigger settings */}
            <div className="card p-5 space-y-4">
              <h3 className="text-sm font-semibold text-slate-900">Trigger Settings</h3>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Auto-trigger on push to</label>
                <input
                  className="input"
                  placeholder="main, release/*"
                  value={pipeline.triggerBranches || ''}
                  onChange={e => setPipeline(p => ({ ...p, triggerBranches: e.target.value }))}
                />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-accent-purple"
                  checked={pipeline.requireApproval || false}
                  onChange={e => setPipeline(p => ({ ...p, requireApproval: e.target.checked }))}
                />
                <div>
                  <span className="text-sm text-slate-900">Require manual approval for production</span>
                  <p className="text-xs text-slate-500">Production deployments need a reviewer to approve</p>
                </div>
              </label>
            </div>
          </div>

          {/* ── Right column: Cloud Build trigger ── */}
          <div className="space-y-5">
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Trigger Build</h3>
              <CloudBuildPanel repo={pipeline.repo} branch={pipeline.branch} />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
